// Turns "Omrekenfactor" into a question a coordinator can answer without ever
// opening the meetstandaard.
//
// WHY. The old field asked for a bare number and never said in which unit. That
// unit is a property of the interventie the material counts towards — ET03 is
// measured in strekkende meter, ET01 in m2 folie — and it lived only in the
// fetched standaard. So the field could only be filled in correctly by someone
// who already knew the standaard by heart.
//
// The question closes that gap by naming both ends of the conversion: the
// registration unit from the standaard, and the product unit the organisation
// itself typed in the Eenheid field.
//
//   'Hoeveel strekkende meter zit er in 1 koker?'
//
// AND IT ONLY ASKS WHEN THERE IS SOMETHING TO ASK. Most interventies count the
// item itself (ET02 per lamp, ET16 per stuk, ET10 per woning). There is no
// conversion to make, and asking anyway is what made the field feel arbitrary.
// Die gevallen krijgen niets: geen invoerveld en geen mededeling. Er stond ooit
// 'Telt mee als X. Omrekenen is niet nodig.', maar dat beantwoordt een vraag die
// niemand gesteld had — het veld dat het geruststelde, staat er immers niet.
//
// Wat overblijft aan notities zijn alleen nog WAARSCHUWINGEN: niet gekoppeld, of
// gekoppeld aan iets zonder kengetal. Die moeten wel op het scherm, want ze
// zeggen dat dit materiaal nergens meetelt. Voeg hier dus geen geruststelling
// terug toe: dan is 'er staat een regel' weer geen signaal meer.
//
// REFUSES TO GUESS. Without the library we do not know the registration unit,
// so the helper asks nothing rather than inventing a neutral word for it.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import resolveMaterialIntervention from './resolveMaterialIntervention'
import isItemCountUnit from './isItemCountUnit'

/**
 * The product unit as it reads in a sentence about a single item. Only 'stuks'
 * needs singularising; the other options ('rol', 'koker', 'm²') already are.
 *
 * @param {string} unit - The Eenheid field of the material
 * @returns {string}
 */
const singularProductUnit = (unit) => {
    const value = (unit || '').trim()
    if (!value || value.toLowerCase() === 'stuks') return 'stuk'
    return value
}

/**
 * @typedef {object} MaterialFactorQuestion
 * @property {boolean} ask - Whether to render the conversion input at all
 * @property {string} question - Label above the input; '' when ask is false
 * @property {string} suffix - Unit line after the input, e.g. 'm2 folie per rol'
 * @property {string|null} note - What to say instead when nothing is asked
 */

/** @type {MaterialFactorQuestion} */
const SILENT = { ask: false, question: '', suffix: '', note: null }

/**
 * @param {{Title?: string, Unit?: string, InterventionCode?: string, UnitsPerItem?: number|string|null}} material
 * @param {{byCode: Object<string, object>}|null} library - From useInterventieLibrary
 * @returns {MaterialFactorQuestion}
 */
const materialFactorQuestion = (material, library) => {
    if (!library?.byCode) return SILENT

    const { status, intervention } = resolveMaterialIntervention(material || {}, library)

    if (status === 'unmapped' || status === 'unknown-code') {
        return {
            ...SILENT,
            note: 'Dit materiaal is niet gekoppeld aan de meetstandaard en telt niet mee in de CO2-berekening.',
        }
    }

    if (status === 'no-kengetal') {
        return {
            ...SILENT,
            note: `De meetstandaard heeft nog geen kengetal voor ${intervention.name}, dus dit materiaal telt niet mee in de CO2-berekening.`,
        }
    }

    // Niets te vragen en niets te melden: het materiaal telt gewoon als zichzelf.
    if (isItemCountUnit(intervention.unit)) return SILENT

    const productUnit = singularProductUnit(material?.Unit)

    return {
        ask: true,
        question: `Hoeveel ${intervention.unit} zit er in 1 ${productUnit}?`,
        suffix: `${intervention.unit} per ${productUnit}`,
        note: null,
    }
}

export default materialFactorQuestion
